import {
  Scene, Mesh, ConeGeometry, MeshPhongMaterial,
  Vector3, Object3D, Color, Group,
} from 'three';
import { IEnemy } from '../enemy/IEnemy';

const MISSILE_SPEED_ADD = 250; // m/s (発射機速度に加算)
const MISSILE_MAX_SPEED = 600; // m/s
const MISSILE_RANGE = 3000;    // m
const HIT_RADIUS = 15;
const TURN_RATE = 2.5;         // rad/s（PN誘導の旋回速度）

interface MissileInst {
  mesh: Group;
  velocity: Vector3;
  target: IEnemy | null;
  life: number;
  active: boolean;
  trail: TrailParticle[];
}

interface TrailParticle {
  mesh: Mesh;
  life: number;
}

export class MissileManager {
  private missiles: MissileInst[] = [];
  maxAmmo = 8;
  ammo = 8;

  private trailGeo = new ConeGeometry(0.3, 1.2, 5);
  private missileMat = new MeshPhongMaterial({ color: new Color(0xdddddd) });
  private trailMat = new MeshPhongMaterial({
    color: new Color(0xffffff), transparent: true, opacity: 0.6,
  });

  constructor(private scene: Scene) {}

  fire(from: Object3D, target: IEnemy): void {
    if (this.ammo <= 0) return;
    this.ammo--;

    const group = new Group();
    const body = new Mesh(this.trailGeo, this.missileMat);
    body.rotation.x = Math.PI / 2;
    group.add(body);
    group.position.copy(from.position);

    // 機首前方に初速を与える
    const forward = new Vector3(0, 0, -1).applyQuaternion(from.quaternion);
    const speed = MISSILE_SPEED_ADD;
    const vel = forward.multiplyScalar(speed);

    group.quaternion.copy(from.quaternion);
    this.scene.add(group);

    this.missiles.push({
      mesh: group,
      velocity: vel,
      target,
      life: MISSILE_RANGE / (speed * 0.5),
      active: true,
      trail: [],
    });
  }

  update(delta: number): void {
    for (const m of this.missiles) {
      if (!m.active) continue;

      m.life -= delta;
      if (m.life <= 0) { this.deactivate(m); continue; }

      // PN誘導（比例航法）: ターゲット方向へ徐々に向く
      if (m.target && m.target.isAlive) {
        const toTarget = m.target.position.clone().sub(m.mesh.position).normalize();
        const currentDir = m.velocity.clone().normalize();
        currentDir.lerp(toTarget, Math.min(TURN_RATE * delta, 1));
        currentDir.normalize();

        const currentSpeed = Math.min(m.velocity.length() + 400 * delta, MISSILE_MAX_SPEED);
        m.velocity.copy(currentDir).multiplyScalar(currentSpeed);
      }

      m.mesh.position.addScaledVector(m.velocity, delta);

      // 向きを速度方向に合わせる
      if (m.velocity.lengthSq() > 0) {
        m.mesh.lookAt(m.mesh.position.clone().add(m.velocity));
      }

      // トレイルパーティクル生成
      if (Math.random() < 0.6) {
        const t = new Mesh(
          new ConeGeometry(0.4 + Math.random() * 0.3, 1.5, 4),
          this.trailMat.clone()
        );
        t.position.copy(m.mesh.position);
        this.scene.add(t);
        m.trail.push({ mesh: t, life: 0.4 });
      }

      // トレイル更新
      for (let i = m.trail.length - 1; i >= 0; i--) {
        const t = m.trail[i];
        t.life -= delta;
        const mat = t.mesh.material as MeshPhongMaterial;
        mat.opacity = Math.max(0, t.life / 0.4 * 0.6);
        t.mesh.scale.multiplyScalar(1 + delta * 3);
        if (t.life <= 0) {
          this.scene.remove(t.mesh);
          t.mesh.geometry.dispose();
          m.trail.splice(i, 1);
        }
      }
    }
  }

  checkHits(enemies: IEnemy[]): { enemy: IEnemy; pos: Vector3 }[] {
    const hits: { enemy: IEnemy; pos: Vector3 }[] = [];
    for (const m of this.missiles) {
      if (!m.active) continue;
      for (const enemy of enemies) {
        if (m.mesh.position.distanceTo(enemy.position) < HIT_RADIUS + enemy.hitRadius) {
          hits.push({ enemy, pos: m.mesh.position.clone() });
          this.deactivate(m);
          break;
        }
      }
    }
    return hits;
  }

  /** アクティブなプレイヤーミサイル位置一覧 (レーダー表示用) */
  getActiveMissilePositions(): Vector3[] {
    return this.missiles
      .filter(m => m.active)
      .map(m => m.mesh.position.clone());
  }

  private deactivate(m: MissileInst): void {
    m.active = false;
    this.scene.remove(m.mesh);
    for (const t of m.trail) {
      this.scene.remove(t.mesh);
      t.mesh.geometry.dispose();
    }
    m.trail = [];
  }
}
